import React from 'react'
import clsx from 'clsx';
import moment from 'moment';
import { Grid, Box, Paper, makeStyles, Avatar, Typography, Chip, Toolbar, AppBar, Fab, Dialog, IconButton, DialogContent, TextField } from '@material-ui/core';
import MainScreen from '../MainScreen';
import { fetchChatRoomList, subscribe, unsubscribe, fetchUserList, fetchChatRoomFor, createGroupChat, } from 'services/chat';
import { useParams, useHistory } from 'react-router-dom';
import { useAuthUserContext } from 'context/AuthContext';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import { ChatRoom, UserCard } from './ChatRoom';

const sleep = delay => new Promise(resolve => setTimeout(resolve, delay));

export const useStyles = makeStyles(theme => ({
  paper: {
    color: theme.palette.text.secondary,
  },
  selectedCard: {
    backgroundColor: theme.palette.action.selected,
  },
  scrollable: {
    flex: '1 1 auto',
    padding: theme.spacing(2),
    overflowX: 'hidden',
    overflowY: 'auto',
    height: `calc(100vh - ${64+64}px)`,
  },
  viewContainer: {
    position: 'relative',
  },
  chip: {
    minWidth: 24,
    maxWidth: '100%',
  },
  titlebar: {
    background: theme.palette.background.paper,
    color: theme.palette.text.secondary,
  },
  fab: {
    position: 'absolute',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
}));

function ChatRoomCard({id, avatar, name, count_notif: counter, last_comment, selected, onClick, ...props}) {
  const classes = useStyles();
  const subtitle = last_comment.message.trim().split(/\n/, 1)[0];
  const [notification, setNotification] = React.useState(null)
  const time_epoch = moment.unix(last_comment.unix_timestamp);
  const today = moment({hour: 0});
  const {typingEvent, room_type, participants} = props;

  React.useEffect(() => {
    if(typingEvent && typingEvent.room_id === `${id}`) {
      const user = (participants && participants.find(user => user.email === typingEvent.username)) || {username: 'Someone'};
      const notification = room_type === 'single' ? 'is typing ...' : `${user.username} is typing ...`;
      setNotification(typingEvent.message === '1' ? notification : '')
    }
  }, [typingEvent, id, participants, room_type]);

  return <Grid item xs={12}>
    <Paper className={clsx(classes.paper, { [classes.selectedCard]: !!selected })}>
      <Box p={1} onClick={onClick}>
        <Grid container alignItems="center">
          <Grid item xs={2}>
            <Box display="flex" alignItems="center" justifyContent="center">
              <Avatar src={avatar} />
            </Box>
          </Grid>
          <Grid item xs={10}>
            <Grid container>
              <Grid item xs={8}> <Typography variant="h6" noWrap>{name}</Typography> </Grid>
              <Grid item xs={4}>
                <Box textAlign="right"> <Typography variant="subtitle2"> {time_epoch.format(time_epoch.isBefore(today) ? 'DD/MM/YYYY': 'HH:mm')} </Typography> </Box>
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={counter > 0 ? 9 : 12}> <Typography variant="subtitle2">{notification || subtitle}</Typography> </Grid>
              {!selected && counter > 0 && <Grid item xs={3}>
                <Box textAlign="right"> <Chip size="small" color="primary" label={counter} className={classes.chip} /> </Box>
              </Grid>}
            </Grid>
          </Grid>
        </Grid>        
      </Box>
    </Paper>
  </Grid>;
}

function NewChatRoomDialog({user, onCreate, onCancel}) {
  const [users, setUsers] = React.useState();
  const [isGroupChat, setIsGroupChat] = React.useState(false);
  const [groupName, setGroupName] = React.useState('');
  const [selectedUsers, setSelectedUsers] = React.useState([]);

  React.useEffect(() => {
    setIsGroupChat(false);
    fetchUserList()
    .then(users => {
      setSelectedUsers([]);
      setUsers(users.filter(u => u.id !== user.userId));
    });
  }, [user.userId]);

  const selectUser = userId => {
    setSelectedUsers(
      selectedUsers.includes(userId) 
      ? selectedUsers.filter(id => id !== userId)
      : [...selectedUsers, userId]
    );
  };

  const newGroupChat = () => createGroupChat(groupName, selectedUsers).then(onCreate);

  const newSingleChat = userId => fetchChatRoomFor(userId).then(onCreate);

  return <Dialog onClose={onCancel} aria-labelledby="simple-dialog-title" open={true}>
    <AppBar position="relative">
      <Toolbar>
          <CloseIcon />
        <IconButton edge="start" color="inherit" onClick={onCancel} aria-label="close">
        </IconButton>
        <Typography variant="h6" noWrap={true} flex={1} spacing={1}>
          {isGroupChat ? `New Group : Select users` : `Select user to chat`}
        </Typography>
      </Toolbar>
    </AppBar>
    <DialogContent>
      {!isGroupChat && <UserCard 
          key={"new"} icon="headcount" name="New Group" onClick={() => setIsGroupChat(true)} 
      />}
      {isGroupChat && <Grid container alignItems="center" justifyContent="center">
        <Grid item xs={10}>
          <TextField placeholder="Group chat name" fullWidth
            value={groupName} 
            onChange={e => setGroupName(e.target.value)}
          /> 
        </Grid>
        <Grid item xs={2}>
          <IconButton onClick={newGroupChat} disabled={!groupName}>
            <CheckIcon />
          </IconButton>
        </Grid>
      </Grid>}
      {users && users.map(({id, avatar, name}) => <UserCard 
        key={id} avatar={avatar} selected={selectedUsers.includes(id)} name={name} onClick={() => isGroupChat ? selectUser(id) : newSingleChat(id)} 
      />)}
    </DialogContent>
  </Dialog>;
}

function ChatRoomsList({currentRoomId, newMessage, messageRead, typingEvent, user}) {
  const classes = useStyles();
  const history = useHistory();
  const [showNewChatRoom, setShowNewChatRoom] = React.useState(false);
  const [chatRooms, setChatRooms] = React.useState();

  React.useEffect(() => {
    sleep(messageRead ? 1000 : 0)
    .then(fetchChatRoomList)
    .then(chatrooms => setChatRooms(chatrooms));
  }, [messageRead, newMessage]);
// TODO: optimize fetching entire rooms list just for unread counts

  return <>
    <AppBar position="relative" elevation={0} className={classes.titlebar}>
      <Toolbar>
        <Box flexGrow={1}>
          <Typography variant="h6" noWrap={true} spacing={1}>Chats</Typography>
        </Box>
      </Toolbar>
    </AppBar>
    <div className={classes.viewContainer}>
      <div className={classes.scrollable}>
        <Grid container spacing={1} style={{padding: 8}}>
          {chatRooms && chatRooms.map(chatRoom => (
            <ChatRoomCard key={chatRoom.id} 
              {...chatRoom} selected={currentRoomId===`${chatRoom.id}`} 
              onClick={() => history.push(`/chat/${chatRoom.id}`)} 
              typingEvent={typingEvent && typingEvent.room_id === `${chatRoom.id}` && typingEvent}
            />
          ))}
        </Grid>
      </div>
      {!showNewChatRoom && <Fab color="primary" className={classes.fab} aria-label="New Chat"
        onClick={() => setShowNewChatRoom(true)}
      >
        <AddIcon />
      </Fab>}
    </div>
    {showNewChatRoom && <NewChatRoomDialog
      user={user} 
      onCreate={chatRoomId => {
        chatRoomId && history.push(`/chat/${chatRoomId}`);
        setShowNewChatRoom(false);
      }}
      onCancel={() => setShowNewChatRoom(false)}
    />}
  </>;
}

export default function Chat(props) {
  const { roomId, command, params } = useParams();
  const history  = useHistory();
  const user = useAuthUserContext();
  const [currentRoomId, setCurrentRoomId] = React.useState(null);
  const [commandObject, setCommandObject] = React.useState(null);
  const [newMessage, setNewMessage] = React.useState(null);
  const [typingEvent, setTypingEvent] = React.useState(null);
  const [messageRead, setMessageRead] = React.useState();
  const onNewMessage = React.useCallback(newMessage => {
    setNewMessage(newMessage);
  }, []);
  const onMessageRead = React.useCallback(messageRead => {
    setMessageRead(messageRead);
  }, []);
  const onTyping = React.useCallback(typingEvent => {
    setTypingEvent(typingEvent);
  }, []);

  React.useEffect(() => {
    subscribe('new-message', onNewMessage);
    subscribe('typing', onTyping);
    return () => {
      unsubscribe('new-message', onNewMessage);
      unsubscribe('typing', onTyping);
    };
  }, [onNewMessage, onTyping]);

  React.useEffect(() => {
    roomId === 'current' && history.replace(history.location.pathname.replace(/\/current/, `/${currentRoomId || ''}`));
    if(roomId && roomId !== 'current') {
      setCurrentRoomId(roomId);
      if(command) {
        setCommandObject({
          command, 
          params: params && params.split('/'), 
          id: moment.now()
        });
        history.replace(`/chat/${roomId}`);
      }
    }
  }, [roomId, history, currentRoomId, command, params]);

  return <MainScreen {...props}>
    <Grid container>
      <Grid item xs={4}>
        <ChatRoomsList currentRoomId={currentRoomId} user={user} 
          newMessage={newMessage} 
          messageRead={messageRead}
          typingEvent={typingEvent}
        />
      </Grid>
      <Grid item xs={8}>
        <ChatRoom roomId={currentRoomId} user={user} 
          newMessage={newMessage && currentRoomId === `${newMessage.room_id}` && newMessage} 
          onMessageRead={onMessageRead}
          commandObject={commandObject}
          typingEvent={typingEvent}
        />
      </Grid>
    </Grid>
  </MainScreen>
  ;
}